/**
 * Files (legacy collection name "designs") CRUD.
 *
 * Web SDK port of mobile's
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/designs/designs.ts
 *   + types.ts
 *
 * One file per entry. No subcollection. Path: `designs/{designId}`.
 * The user-facing label is "Files" — the legacy "designs" collection
 * name is kept so the Firestore rule (lines 763-785) + index + mobile
 * client all keep working without migration.
 *
 * Schema parity (verified against mobile types.ts):
 *   - orgId, projectId
 *   - title (required), description (optional)
 *   - category (`design_2d` / `design_3d` / `layout` / `mom` /
 *     `agreement` / `other`)
 *   - fileUrl, fileKey (R2 object key for delete), fileContentType,
 *     fileSizeBytes, fileName?
 *   - thumbnailUrl?: fileUrl when image, null for PDFs/other
 *   - createdBy / createdByName / createdAt / updatedAt
 */
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Timestamp,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

// ───── Categories (verbatim from mobile types.ts) ─────

export const FILE_CATEGORIES = [
  { key: 'design_2d', label: '2D' },
  { key: 'design_3d', label: '3D' },
  { key: 'layout',    label: 'Layout' },
  { key: 'mom',       label: 'MOM' },
  { key: 'agreement', label: 'Agreement' },
  { key: 'other',     label: 'Other' },
] as const;

export type FileCategory = (typeof FILE_CATEGORIES)[number]['key'];

export function getCategoryLabel(key: FileCategory | undefined): string {
  return FILE_CATEGORIES.find((c) => c.key === key)?.label ?? 'Other';
}

// ───── Doc shapes ─────

export type DesignFilePayload = {
  /** Public R2 URL — what the app renders / opens. */
  url: string;
  /** R2 object key — used to delete the file later. */
  key: string;
  /** MIME type, e.g. 'image/jpeg' or 'application/pdf'. */
  contentType: string;
  /** Size in bytes. */
  sizeBytes: number;
  /** Original file name from the picker, when available. */
  name?: string;
};

export type Design = {
  id: string;
  orgId: string;
  projectId: string;
  title: string;
  description?: string;
  category: FileCategory;
  // Inline file fields
  fileUrl: string;
  fileKey: string;
  fileContentType: string;
  fileSizeBytes: number;
  fileName?: string;
  /** Same as fileUrl when the file is an image; null for PDFs. */
  thumbnailUrl?: string | null;
  // Audit
  createdBy: string;
  createdByName?: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
};

// ───── CRUD ─────

export type CreateDesignInput = {
  orgId: string;
  projectId: string;
  title: string;
  description?: string;
  category: FileCategory;
  file: DesignFilePayload;
  createdBy: string;
  createdByName?: string;
};

export async function createDesign(
  input: CreateDesignInput,
): Promise<string> {
  const ref = doc(collection(db, 'designs'));
  const payload: Record<string, unknown> = {
    orgId: input.orgId,
    projectId: input.projectId,
    title: input.title.trim() || 'Untitled file',
    category: input.category,
    fileUrl: input.file.url,
    fileKey: input.file.key,
    fileContentType: input.file.contentType,
    fileSizeBytes: input.file.sizeBytes,
    thumbnailUrl: input.file.contentType.startsWith('image/')
      ? input.file.url
      : null,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (input.description?.trim()) payload.description = input.description.trim();
  if (input.file.name) payload.fileName = input.file.name;
  if (input.createdByName) payload.createdByName = input.createdByName;
  await setDoc(ref, payload);
  return ref.id;
}

export type UpdateDesignInput = {
  title?: string;
  description?: string;
  category?: FileCategory;
  /** Optional new file. When set, overwrites every file-* field on
   *  the doc and updates the thumbnail. The OLD R2 key is the
   *  caller's responsibility to delete via `deleteFromR2`. */
  file?: DesignFilePayload;
};

export async function updateDesign(
  designId: string,
  input: UpdateDesignInput,
): Promise<void> {
  const patch: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };
  if (input.title !== undefined) {
    patch.title = input.title.trim() || 'Untitled file';
  }
  if (input.category !== undefined) patch.category = input.category;
  if (input.description !== undefined) {
    patch.description = input.description.trim() || deleteField();
  }
  if (input.file) {
    patch.fileUrl = input.file.url;
    patch.fileKey = input.file.key;
    patch.fileContentType = input.file.contentType;
    patch.fileSizeBytes = input.file.sizeBytes;
    patch.fileName = input.file.name ?? deleteField();
    patch.thumbnailUrl = input.file.contentType.startsWith('image/')
      ? input.file.url
      : null;
  }
  await updateDoc(doc(db, 'designs', designId), patch);
}

export async function deleteDesign(designId: string): Promise<void> {
  await deleteDoc(doc(db, 'designs', designId));
}

// ───── Display helpers ─────

/** Phosphor icon + accent colour for a given MIME type. Matches the
 *  design folder's per-file-type colouring (PDF red, image blue,
 *  CAD/code green, generic gray). */
export function fileTypeIcon(contentType: string, fileName?: string): {
  icon: string;
  color: string;
} {
  const ct = (contentType || '').toLowerCase();
  const ext = (fileName ?? '').toLowerCase().split('.').pop() ?? '';
  if (ct === 'application/pdf' || ext === 'pdf') {
    return { icon: 'ph ph-file-pdf', color: 'var(--critical)' };
  }
  if (ct.startsWith('image/')) {
    return { icon: 'ph ph-file-image', color: '#2563EB' };
  }
  if (['dwg', 'dxf', 'rvt', 'skp', '3ds', 'obj'].includes(ext)) {
    return { icon: 'ph ph-file-code', color: '#059669' };
  }
  if (ct.startsWith('video/')) {
    return { icon: 'ph ph-file-video', color: '#9333EA' };
  }
  if (ct.startsWith('audio/')) {
    return { icon: 'ph ph-file-audio', color: '#0EA5E9' };
  }
  if (
    ct === 'application/zip' ||
    ct === 'application/x-rar-compressed' ||
    ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)
  ) {
    return { icon: 'ph ph-file-zip', color: '#A16207' };
  }
  if (
    ct === 'application/msword' ||
    ct ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    ['doc', 'docx'].includes(ext)
  ) {
    return { icon: 'ph ph-file-doc', color: '#1D4ED8' };
  }
  if (
    ct === 'application/vnd.ms-excel' ||
    ct ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    ['xls', 'xlsx', 'csv'].includes(ext)
  ) {
    return { icon: 'ph ph-file-xls', color: '#15803D' };
  }
  return { icon: 'ph ph-file', color: 'var(--text-secondary)' };
}

/** "1.2 MB", "456 KB", "42 B". */
export function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** Short relative date — "just now" / "12m ago" / "3h ago" /
 *  "2d ago" / "15 May" for anything older than 30 days. */
export function formatDesignRelTime(d: Date | null): string {
  if (!d) return '—';
  const ms = Date.now() - d.getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}
